import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Map as MapLibreGLMap,
  type MapLibreMap,
  type Marker,
  NavigationControl,
  ScaleControl,
} from 'maplibre-gl';
import type { BBox, MapLocation, RadarFrame, RadarTileOptions } from '@repo/shared';
import { type BasemapId, resolveBasemap } from '../lib/basemap';
import { syncBasemapDim } from '../lib/basemap-dim';
import { useBasemapStyle } from '../features/basemap/hooks';
import { selectFrameWindow, showRadarFrame, syncRadarLayers } from '../lib/radar-layers';
import { syncCoverageLayer } from '../lib/coverage-layer';
import { addTargetMarker, removeSavedMarkers, syncSavedMarkers } from '../lib/map-markers';
import 'maplibre-gl/dist/maplibre-gl.css';
import styles from './MapView.module.css';

/** Zoom used when flying to a point that has no extent of its own. */
const POINT_ZOOM = 9;

export interface MapViewport {
  longitude: number;
  latitude: number;
  zoom: number;
}

export interface MapTarget extends MapLocation {
  /** Present for areas — the map fits these bounds instead of guessing a zoom. */
  bbox?: BBox;
  /**
   * How this place was arrived at. A device fix and a searched address get
   * different marks: one is a measurement, the other is a name.
   */
  kind?: 'search' | 'located';
}

/** A saved place, as the map needs it: a location that can be clicked back to. */
export interface MapSavedLocation extends MapLocation {
  id: string;
}

export interface MapViewProps {
  /** Empty string selects the keyless fallback basemap. */
  mapboxToken: string;
  basemapId: BasemapId;
  /** 0–1. How far the basemap is pushed back behind the radar. */
  basemapDim: number;
  /** Where the map opens. Read once — later changes come through `target`. */
  initialViewport: MapViewport;
  frames: readonly RadarFrame[];
  activeFrameIndex: number;
  radarOpacity: number;
  tileOptions: RadarTileOptions;
  /** Dims the areas no radar observes, so a clear map can't be misread as dry. */
  showCoverage: boolean;
  /** The place to move to. A new object identity triggers the move. */
  target: MapTarget | null;
  savedLocations: readonly MapSavedLocation[];
  onSelectSaved: (location: MapSavedLocation) => void;
  onViewportChange?: (viewport: MapViewport) => void;
}

/**
 * Owns the MapLibre instance.
 *
 * MapLibre maintains its own mutable scene graph, so this component
 * deliberately renders one empty div and never re-renders it. React's job here
 * is to translate prop changes into imperative map calls — creating the map
 * once, then pushing radar layers, markers and camera moves into it.
 */
export function MapView({
  mapboxToken,
  basemapId,
  basemapDim,
  initialViewport,
  frames,
  activeFrameIndex,
  radarOpacity,
  tileOptions,
  showCoverage,
  target,
  savedLocations,
  onSelectSaved,
  onViewportChange,
}: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const markerRef = useRef<Marker | null>(null);
  /*
   * The map whose style has parsed — the identity of it, not a flag.
   *
   * Switching basemap tears the old map down and builds a new one, and the
   * obvious `isStyleReady` boolean cannot express that. The teardown sets it
   * false and the new map's `style.load` sets it true, but those two updates
   * land in the same React batch, so the state goes true → true, nothing
   * re-renders, and every effect that mounts something onto the map — the
   * dimmer, the coverage scrim, the pin — quietly never runs again. The map
   * comes back with only the layers that happen to be re-synced by the
   * animation ticking.
   *
   * An instance changes identity even when the null between two maps is
   * collapsed away, so effects keyed on it re-run exactly once per map.
   */
  const [readyMap, setReadyMap] = useState<MapLibreMap | null>(null);

  /*
   * The same readiness, synchronously.
   *
   * React also runs this component's other effects in the *teardown* commit,
   * before any of the above has rendered, so an effect whose deps changed with
   * the basemap would fire against a map whose style has not parsed — and
   * MapLibre answers `addLayer` with "Style is not done loading". The state is
   * what re-runs the effects; the ref is what makes them safe in that window.
   */
  const isStyleReadyRef = useRef(false);

  /** The map, but only once its style has parsed and layers can be added. */
  const styledMap = useCallback(() => (isStyleReadyRef.current ? mapRef.current : null), []);

  /**
   * Where the next map gets built.
   *
   * Seeded from the opening viewport and then kept current on every `moveend`.
   * Switching basemap constructs a *new* map (see the effect below), and one
   * built from the mount-time viewport teleports the viewer back to wherever
   * they opened the app — silently, because the constructor fires no `moveend`,
   * so the URL would go on advertising the view they had a moment ago.
   */
  const initialViewportRef = useRef(initialViewport);
  const onViewportChangeRef = useRef(onViewportChange);
  onViewportChangeRef.current = onViewportChange;
  const onSelectSavedRef = useRef(onSelectSaved);
  onSelectSavedRef.current = onSelectSaved;

  const basemap = useMemo(() => resolveBasemap(mapboxToken, basemapId), [mapboxToken, basemapId]);
  const { data: style } = useBasemapStyle(basemap, mapboxToken);

  useEffect(() => {
    const container = containerRef.current;
    // The style is fetched up front so Mapbox's glyph template can be rewritten
    // before MapLibre's worker tries to use it — see lib/basemap.ts.
    if (!container || !style) return;

    const { longitude, latitude, zoom } = initialViewportRef.current;

    const map = new MapLibreGLMap({
      container,
      style,
      validateStyle: basemap.validateStyle,
      transformRequest: basemap.transformRequest,
      center: [longitude, latitude],
      zoom,
      // Radar tiles stop resolving usefully past this.
      maxZoom: 14,
      // No maxBounds: constraining it to the whole world produces a singular
      // projection matrix in MapLibre's constrain pass and throws on init.
      // MapLibre already clamps latitude to the Web Mercator limit anyway.
      attributionControl: { compact: true },
    });

    mapRef.current = map;

    // Reaching the map instance from the console is the only practical way to
    // debug a render problem — the scene graph is invisible to React devtools.
    if (import.meta.env.DEV) {
      (window as unknown as { map: MapLibreMap }).map = map;
    }

    map.on('error', ({ error }) => console.error('[map]', error?.message ?? error));

    map.addControl(new NavigationControl({ showCompass: false }), 'top-right');
    map.addControl(new ScaleControl({ unit: 'imperial' }), 'bottom-left');

    // `style.load` fires once the style graph is parsed, which is all that
    // adding sources and layers requires. The `load` event additionally waits
    // for every basemap tile to arrive — gating on it would leave the radar
    // hidden behind a slow or partly-failing basemap.
    map.on('style.load', () => {
      isStyleReadyRef.current = true;
      setReadyMap(map);
    });
    map.on('moveend', () => {
      const center = map.getCenter();
      const viewport = {
        longitude: center.lng,
        latitude: center.lat,
        zoom: map.getZoom(),
      };

      // A rebuild resumes from here, not from where the app was opened.
      initialViewportRef.current = viewport;
      onViewportChangeRef.current?.(viewport);
    });

    return () => {
      markerRef.current?.remove();
      markerRef.current = null;
      removeSavedMarkers(map);
      map.remove();
      mapRef.current = null;
      isStyleReadyRef.current = false;
      setReadyMap(null);
    };
    // Switching basemap rebuilds the map rather than calling `setStyle`:
    // `setStyle` drops every source and layer while the module-level bookkeeping
    // in radar-layers.ts survives, which would leave the radar silently absent.
  }, [basemap, style]);

  const activeFrame = frames[activeFrameIndex];

  // Only the frames near the active one stay mounted — see radar-layers.ts for
  // why mounting the whole loop at once trips RainViewer's rate limit.
  const windowedFrames = useMemo(
    () => selectFrameWindow(frames, activeFrameIndex),
    [frames, activeFrameIndex],
  );

  // Mounted first, and deliberately before the radar: every overlay inserts
  // itself below the first symbol layer, so whatever is added last sits on top.
  // The dimmer has to stay under the data it exists to make readable.
  useEffect(() => {
    const map = styledMap();
    if (!map) return;

    syncBasemapDim(map, { opacity: basemapDim });
  }, [readyMap, styledMap, basemapDim]);

  useEffect(() => {
    const map = styledMap();
    if (!map || windowedFrames.length === 0) return;

    syncRadarLayers(map, { frames: windowedFrames, tileOptions });
  }, [readyMap, styledMap, windowedFrames, tileOptions]);

  useEffect(() => {
    const map = styledMap();
    if (!map) return;

    syncCoverageLayer(map, {
      visible: showCoverage,
      tileSize: tileOptions.tileSize,
      // How heavy an opaque-black mask reads depends on what's under it, so the
      // basemap decides — see `coverageScrim` in lib/basemap.ts.
      opacity: basemap.coverageScrim,
    });
  }, [readyMap, styledMap, showCoverage, tileOptions.tileSize, basemap.coverageScrim]);

  // Reveal exactly one frame. Runs on every animation tick.
  useEffect(() => {
    const map = styledMap();
    if (!map || !activeFrame) return;

    showRadarFrame(map, activeFrame.time, radarOpacity);
  }, [readyMap, styledMap, activeFrame, radarOpacity]);

  /*
   * Mark the chosen place, and fly to it — but only when it is newly chosen.
   *
   * This effect also runs when the map is rebuilt for a new basemap, because a
   * rebuild takes the pin with it and it has to be put back. The camera is a
   * different matter: flying again would drag the viewer back to whatever they
   * last searched for, undoing however far they had panned since. So the pin is
   * re-added on every run and the camera moves only when `target` is actually a
   * new place.
   */
  const flownToRef = useRef<MapTarget | null>(null);

  useEffect(() => {
    const map = styledMap();
    if (!map || !target) return;

    const isNewPlace = flownToRef.current !== target;

    if (isNewPlace) {
      flownToRef.current = target;

      if (target.bbox) {
        const [west, south, east, north] = target.bbox;
        map.fitBounds(
          [
            [west, south],
            [east, north],
          ],
          { padding: 64, maxZoom: POINT_ZOOM + 2, essential: true },
        );
      } else {
        map.flyTo({
          center: [target.longitude, target.latitude],
          zoom: Math.max(map.getZoom(), POINT_ZOOM),
          essential: true,
        });
      }
    }

    markerRef.current?.remove();
    markerRef.current = addTargetMarker(map, target, {
      kind: target.kind ?? 'search',
      // The drop animation belongs to arriving somewhere, not to the pin being
      // rebuilt underneath a viewer who only changed the basemap.
      animate: isNewPlace,
    });
  }, [readyMap, styledMap, target]);

  // Saved places are marked too — seeing them on the map is most of the point
  // of having saved them. Clicking one flies there.
  useEffect(() => {
    const map = styledMap();
    if (!map) return;

    syncSavedMarkers(map, savedLocations, (location) => onSelectSavedRef.current(location));
  }, [readyMap, styledMap, savedLocations]);

  return <div ref={containerRef} className={styles.map} data-testid="map-container" />;
}
